import React, { useState, useEffect, useRef } from 'react';
import { Box, Image, Text, Button, Badge, Flex, IconButton, useToast, useMediaQuery, Skeleton } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaHeart, FaList, FaClock } from 'react-icons/fa';
import { addToFavorites, removeFromFavorites, fetchFavorites } from '../../../Redux/Favorites/actions';
import { addToWatchlist, removeVideoFromQueue, fetchUserQueues } from '../../../Redux/ViewQueue/action';
import { addToWatchLater, removeFromWatchLater, fetchWatchLater } from '../../../Redux/WatchLater/actions';
import BurrowSpinner from '../../UI/BurrowSpinner';
import { getOptimizedThumbnail } from '../../../utils/imageOptimizer';
import { useLazyLoad } from '../../../hooks/useLazyLoad';
import { buildVideoUrl } from '../../../utils/slugify';
import useRiseShatter from '../../../hooks/useRiseShatter';

const VideoCard = ({ video, width = "299px", height = "168px", titleHeight = "24px", fontSize = "16px", isRowHovered = false, isSameTagRow = false, onClick, showOverlayControls = true, disableHoverEffects = false, forceHover = false, isMobileWatchLater = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  // Rise-shatter animation
  const triggerRiseShatter = useRiseShatter();
  const favBtnRef = useRef(null);
  const watchLaterBtnRef = useRef(null);
  const watchlistBtnRef = useRef(null);

  // Active-state selectors (check if video is already in collection)
  const videoId = video?.id;
  const isInFavorites = useSelector(state =>
    state.favorites?.favorites?.videos?.some(v => v.videoId === videoId) || false
  );
  const isInWatchLater = useSelector(state =>
    state.watchLater?.watchLater?.videos?.some(v => v.videoId === videoId) || false
  );
  const isInWatchlist = useSelector(state => {
    const queues = state.viewQueue?.userQueues || [];
    const wl = queues.find(q => q.isWatchlist);
    return wl?.videos?.some(v => v.videoId === videoId) || false;
  });
  const watchlistQueueId = useSelector(state => {
    const queues = state.viewQueue?.userQueues || [];
    return queues.find(q => q.isWatchlist)?.viewQueueID || null;
  });
  
  // Lazy load hook - only render video card when it's near viewport
  const { ref: lazyRef, isVisible, hasBeenVisible } = useLazyLoad({ 
    rootMargin: '200px',
    triggerOnce: true 
  });
  
  // Clear selection if another card is clicked
  useEffect(() => {
    if (!isHovering && !forceHover && !isRowHovered) {
      setIsSelected(false);
    }
  }, [isHovering, forceHover, isRowHovered]);

  const effectiveHoverState = !disableHoverEffects && (isHovering || forceHover || (isMobile && isSelected));
  
  if (!video) return null;

  // Handle navigation when clicking on the video card
  const handleClick = () => {
    if (isMobile) {
      if (isSelected) {
        // If already selected, navigate to the details page
        if (video.id) {
          navigate(buildVideoUrl(video.id, video.title));
        }
      } else {
        // Just select the card, don't navigate
        setIsSelected(true);
      }
    } else {
      // Desktop behavior remains the same
      if (video.id) {
        navigate(buildVideoUrl(video.id, video.title));
      }
    }
  };
  
  // Add handler for favorites — toggles add/remove
  const handleAddToFavorites = (e) => {
    e.stopPropagation();
    if (isInFavorites) {
      triggerRiseShatter(favBtnRef.current, '✕ Removed', '#FC8181');
      dispatch(removeFromFavorites(video.id))
        .then(() => dispatch(fetchFavorites()))
        .catch(() => {
          toast({ title: "Error", description: "Failed to remove from favorites.", status: "error", duration: 3000, isClosable: true });
        });
      return;
    }
    const videoToAdd = {
      videoId: video.id,
      title: video.title,
      shortDescription: video.shortDescription || video.description || '',
      longDescription: video.longDescription || video.description || '',
      thumbnailUrl: video.thumbnailUrl || video.thumbnail || '',
      duration: video.duration || video.length || 0
    };
    triggerRiseShatter(favBtnRef.current, '♥ Favorited', '#FC8181');
    dispatch(addToFavorites(videoToAdd))
      .then(() => dispatch(fetchFavorites()))
      .catch(() => {
        toast({ title: "Error", description: "Failed to add to favorites.", status: "error", duration: 3000, isClosable: true });
      });
  };
  
  // Handle adding/removing from watchlist queue — toggles
  const handleQueue = (e, queueType) => {
    e.stopPropagation();
    if (queueType === 'watchlist') {
      if (isInWatchlist && watchlistQueueId) {
        triggerRiseShatter(watchlistBtnRef.current, '✕ Removed', '#4FD1C5');
        dispatch(removeVideoFromQueue(watchlistQueueId, video.id))
          .then(() => dispatch(fetchUserQueues()))
          .catch(() => {
            toast({ title: "Error", description: "Failed to remove from watchlist.", status: "error", duration: 3000, isClosable: true });
          });
        return;
      }
      const videoToAdd = {
        videoId: video.id,
        title: video.title || 'Untitled Video',
        thumbnailUrl: video.thumbnailUrl || video.thumbnail || '',
        duration: video.duration || video.length || 0
      };
      triggerRiseShatter(watchlistBtnRef.current, '📋 Queued', '#4FD1C5');
      dispatch(addToWatchlist(videoToAdd))
        .then(result => {
          dispatch(fetchUserQueues());
          if (result && !result.success) {
            toast({ title: "Error", description: result?.error || "Failed to add to watchlist.", status: "error", duration: 3000, isClosable: true });
          }
        });
    }
  };
  
  // Watch Later handler — toggles add/remove
  const handleWatchLater = (e) => {
    e.stopPropagation();
    if (isInWatchLater) {
      triggerRiseShatter(watchLaterBtnRef.current, '✕ Removed', '#F6AD55');
      dispatch(removeFromWatchLater(video.id))
        .then(() => dispatch(fetchWatchLater()))
        .catch(() => {
          toast({ title: "Error", description: "Failed to remove from watch later.", status: "error", duration: 3000, isClosable: true });
        });
      return;
    }
    const videoToAdd = {
      videoId: video.id,
      title: video.title,
      shortDescription: video.shortDescription || video.description || '',
      longDescription: video.longDescription || video.description || '',
      thumbnailUrl: video.thumbnailUrl || video.thumbnail || '',
      duration: video.duration || video.length || 0
    };
    triggerRiseShatter(watchLaterBtnRef.current, '⏰ Watch Later', '#F6AD55');
    dispatch(addToWatchLater(videoToAdd))
      .then(() => dispatch(fetchWatchLater()))
      .catch(() => {
        toast({ title: "Error", description: "Failed to add to watch later.", status: "error", duration: 3000, isClosable: true });
      });
  };
  
  // Get duration from various possible locations in the video object
  const getDuration = () => {
    // Check all possible locations for duration data
    if (video.content && video.content.duration) {
      return video.content.duration;
    }
    if (video.duration) {
      return video.duration;
    }
    if (video.length) {
      return video.length;
    }
    return null;
  };
  
  // Format duration to minutes:seconds
  const formatDuration = (duration) => {
    if (!duration) return null;
    
    // If duration is already a string in format like "3:45"
    if (typeof duration === 'string' && duration.includes(':')) {
      return duration;
    }
    
    // If duration is in seconds (number)
    const seconds = Number(duration);
    if (isNaN(seconds)) return null;
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };
  
  // Function to get the correct thumbnail URL
  // Function to get the CDN thumbnail URL (live mode)
  const getThumbnailUrl = () => {
    if (!video) return "https://bunny-burrow-pull-1.b-cdn.net/Default-thumbnail.png";
    
    if (video.thumbnailUrl) {
      return video.thumbnailUrl.startsWith('http') 
        ? video.thumbnailUrl 
        : `https://bunny-burrow-pull-1.b-cdn.net/${video.thumbnailUrl}`;
    }
    
    if (video.thumbnail) {
      return video.thumbnail.startsWith('http') 
        ? video.thumbnail 
        : `https://bunny-burrow-pull-1.b-cdn.net/${video.thumbnail}`;
    }
    
    // Default placeholder
    return "https://bunny-burrow-pull-1.b-cdn.net/Default-thumbnail.png";
  };
  
  // New function to classify video type based on duration
  const getVideoType = (durationInSeconds) => {
    if (!durationInSeconds) return null;
    
    const duration = Number(durationInSeconds);
    if (isNaN(duration)) return null;
    
    // Define cut-off times in seconds
    const CLIP_CUTOFF = 300;   // 5 minutes — clips are under 5 min
    const SHORTS_CUTOFF = 900; // 15 minutes — shorts are 5-15 min
    
    if (duration <= CLIP_CUTOFF) {
      return 'clip';
    } else if (duration <= SHORTS_CUTOFF) {
      return 'short';
    } else {
      return 'film';
    }
  };
  
  // Function to get badge color based on video type
  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'short':
        return 'pink';
      case 'clip':
        return 'purple';
      case 'film':
        return 'blue';
      default:
        return 'gray';
    }
  };
  
  // Get raw duration in seconds and formatted values
  const duration = getDuration();
  const formattedDuration = formatDuration(duration);
  const videoType = getVideoType(duration);
  
  // Get video quality if available
  const quality = video.quality || (video.content && video.content.quality) || null;
  
  // Get thumbnail URL with optimization
  const rawThumbnailUrl = getThumbnailUrl();
  const thumbnailUrl = getOptimizedThumbnail(rawThumbnailUrl);
  
  // Show skeleton placeholder until visible
  if (!hasBeenVisible) {
    return (
      <Box 
        ref={lazyRef}
        display="flex"
        flexDirection="column"
        width={width}
        pb={2}
        px={1}
      >
        <Skeleton 
          width="100%" 
          height={height} 
          borderRadius="md"
          startColor="gray.700"
          endColor="gray.600"
        />
        <Skeleton height={titleHeight} mt={1} width="80%" />
      </Box>
    );
  }
  
  return (
    <Box 
      ref={lazyRef}
      display="flex"
      flexDirection="column"
      width={width}
      pb={2}
      px={1}
      position="relative"
      onMouseEnter={() => !disableHoverEffects && setIsHovering(true)}
      onMouseLeave={() => !disableHoverEffects && setIsHovering(false)}
      onClick={onClick || handleClick}
    >
      {/* Thumbnail Container */}
      <Box 
        className="image-hover-effect permanent-shadow"
        cursor="pointer"
        borderRadius="md"
        overflow="hidden"
        position="relative"
        width="100%"
        height={height}
        transition="all 0.2s ease-out"
        boxShadow="0 6px 12px rgba(0,0,0,0.2)"
        onMouseEnter={() => !disableHoverEffects && setIsHovering(true)}
        onMouseLeave={() => !disableHoverEffects && setIsHovering(false)}
        _hover={{
          transform: "scale(1.03)",
          boxShadow: "0 10px 20px rgba(0,0,0,0.3)",
          zIndex: 10
        }}
      >
        {imageLoading && (
          <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex="1">
            <BurrowSpinner size="xl" />
          </Box>
        )}
        
        <Image
          src={thumbnailUrl}
          alt={video.title || "Video"}
          width="100%"
          height="calc(100% - 2px)"  // Reduce by 2px (1px top + 1px bottom)
          objectFit="cover"  // Changed from "contain" to "cover"
          bg="black"
          fallbackSrc="https://bunny-burrow-pull-1.b-cdn.net/Default-thumbnail.png"
          onLoad={() => setImageLoading(false)}
          onError={(e) => {
            console.error(`Error loading image: ${thumbnailUrl}`);
            e.target.src = "https://bunny-burrow-pull-1.b-cdn.net/Default-thumbnail.png";
            setImageLoading(false);
            setImageError(true);
          }}
          style={{
            marginTop: '1px',  // Add 1px margin to center vertically
            marginBottom: '1px',  // Add 1px margin to center vertically
            opacity: imageLoading ? 0 : 1,
            transition: 'opacity 0.3s ease-in'
          }}
        />
        
        {/* INFO BADGES - Always render but control visibility with opacity */}
        {!isMobileWatchLater && (
          <>
            {/* Mobile version - static display without animation */}
            {isMobile && (
              <Box
                position="absolute"
                top={isSameTagRow ? "42%" : "45%"}
                left="1%"
                transform="translateY(-50%)" 
                bg="transparent" 
                color="white"
                px={2}
                py={1}
                fontSize="xs"
                fontWeight="bold"
                zIndex={5}
                textShadow="0px 0px 3px #000000, 0px 0px 5px #000000"
                opacity={effectiveHoverState ? 1 : 0}
                transition="opacity 0.2s ease-out"
              >
                {formattedDuration && `${formattedDuration}`}
              </Box>
            )}
            
            {/* Desktop version - with animation (slides in from left) */}
            {!isMobile && (
              <Box
                position="absolute"
                top={isSameTagRow ? "37%" : "40%"}
                left="1%"
                bg="transparent" 
                color="white"
                px={2}
                py={1}
                fontSize="sm"
                fontWeight="bold"
                zIndex={5}
                textShadow="0px 0px 3px #000000, 0px 0px 5px #000000"
                style={{
                  opacity: effectiveHoverState ? 1 : 0,
                  transform: effectiveHoverState ? 'translateX(0) translateY(-50%)' : 'translateX(-30px) translateY(-50%)',
                  transition: 'opacity 0.35s ease-out, transform 0.35s ease-out'
                }}
              >
                {formattedDuration && `Duration: ${formattedDuration}`}
              </Box>
            )}
          </>
        )}

        {/* QUALITY BADGE - Right side */}
        {quality && (
          <>
            {/* Mobile version - static display without animation */}
            {isMobile && (
              <Box
                position="absolute"
                top={isSameTagRow ? "37%" : "40%"}
                right="10%"
                transform="translateY(-50%)" 
                bg="transparent" 
                color="white"
                px={2}
                py={1}
                fontSize="xs"
                fontWeight="bold"
                zIndex={5}
                textShadow="0px 0px 3px #000000, 0px 0px 5px #000000"
                opacity={effectiveHoverState ? 1 : 0}
                transition="opacity 0.2s ease-out"
              >
                {quality}
              </Box>
            )}
            
            {/* Desktop version - with animation (slides in from left) */}
            {!isMobile && (
              <Box
                position="absolute"
                top={isSameTagRow ? "37%" : "40%"}
                right="10%"
                bg="transparent" 
                color="white"
                px={2}
                py={1}
                fontSize="sm"
                fontWeight="bold"
                zIndex={5}
                textShadow="0px 0px 3px #000000, 0px 0px 5px #000000"
                opacity={effectiveHoverState ? 1 : 0}
                transform={effectiveHoverState ? "translateX(0) translateY(-50%)" : "translateX(-30px) translateY(-50%)"}
                sx={{
                  transition: 'opacity 0.35s ease-out, transform 0.35s ease-out'
                }}
              >
                {quality}
              </Box>
            )}
          </>
        )}
        
        {/* ACTION BUTTONS - Always render but control visibility */}
        <Box
          position="absolute"
          top="6px"
          left="6px"
          right="6px"
          zIndex={10}
          display="flex"
          justifyContent="space-around"
          gap="4px"
          style={{
            opacity: effectiveHoverState ? 1 : 0,
            transform: effectiveHoverState ? 'translateY(0)' : 'translateY(-15px)',
            transition: 'opacity 0.35s ease-out, transform 0.35s ease-out'
          }}
        >
          {/* Watchlist button */}
          <Box
            ref={watchlistBtnRef}
            as="button"
            aria-label={isInWatchlist ? "Remove from Watchlist" : "Add to Watchlist"}
            onClick={(e) => { e.stopPropagation(); handleQueue(e, 'watchlist'); }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w={parseInt(width) <= 240 ? "18px" : "auto"}
            h={parseInt(width) <= 240 ? "18px" : "auto"}
            minW={parseInt(width) <= 240 ? "18px" : undefined}
            px={parseInt(width) <= 240 ? 0 : 2}
            py={parseInt(width) <= 240 ? 0 : 1}
            bg={isInWatchlist ? "rgba(66, 165, 245, 0.35)" : "rgba(20, 30, 70, 0.85)"}
            color="#42A5F5"
            borderRadius={parseInt(width) <= 240 ? "full" : "md"}
            fontSize="xs"
            fontWeight="bold"
            boxShadow={isInWatchlist ? "0 0 8px rgba(66, 165, 245, 0.9)" : "0 0 5px rgba(66, 165, 245, 0.7)"}
            transition="all 0.3s"
            textShadow={parseInt(width) > 240 ? "0 0 5px rgba(66, 165, 245, 0.7)" : undefined}
            _hover={{ 
              bg: "rgba(66, 165, 245, 0.8)",
              color: "white",
              transform: "scale(1.1)",
              boxShadow: "0 0 10px #42A5F5",
            }}
          >
            {parseInt(width) <= 240 ? <FaList size="9px" /> : (isInWatchlist ? "✓ Listed" : "+ Watchlist")}
          </Box>

          {/* Favorites button */}
          <Box
            ref={favBtnRef}
            as="button"
            aria-label={isInFavorites ? "Remove from Favorites" : "Add to Favorites"}
            onClick={(e) => { e.stopPropagation(); handleAddToFavorites(e); }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w={parseInt(width) <= 240 ? "18px" : "auto"}
            h={parseInt(width) <= 240 ? "18px" : "auto"}
            minW={parseInt(width) <= 240 ? "18px" : undefined}
            px={parseInt(width) <= 240 ? 0 : 2}
            py={parseInt(width) <= 240 ? 0 : 1}
            bg={isInFavorites ? "rgba(206, 147, 216, 0.35)" : "rgba(30, 20, 50, 0.85)"}
            color="#CE93D8"
            borderRadius={parseInt(width) <= 240 ? "full" : "md"}
            fontSize="xs"
            fontWeight="bold"
            boxShadow={isInFavorites ? "0 0 8px rgba(206, 147, 216, 0.9)" : "0 0 5px rgba(206, 147, 216, 0.7)"}
            transition="all 0.3s"
            textShadow={parseInt(width) > 240 ? "0 0 5px rgba(206, 147, 216, 0.7)" : undefined}
            _hover={{ 
              bg: "rgba(206, 147, 216, 0.8)",
              color: "white",
              transform: "scale(1.1)",
              boxShadow: "0 0 10px #CE93D8",
            }}
          >
            {parseInt(width) <= 240 ? <FaHeart size="9px" /> : (isInFavorites ? "✓ Favorited" : "+ Favorites")}
          </Box>

          {/* Watch Later button */}
          <Box
            ref={watchLaterBtnRef}
            as="button"
            aria-label={isInWatchLater ? "Remove from Watch Later" : "Add to Watch Later"}
            onClick={(e) => { e.stopPropagation(); handleWatchLater(e); }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            w={parseInt(width) <= 240 ? "18px" : "auto"}
            h={parseInt(width) <= 240 ? "18px" : "auto"}
            minW={parseInt(width) <= 240 ? "18px" : undefined}
            px={parseInt(width) <= 240 ? 0 : 2}
            py={parseInt(width) <= 240 ? 0 : 1}
            bg={isInWatchLater ? "rgba(77, 182, 172, 0.35)" : "rgba(20, 50, 50, 0.85)"}
            color="#4DB6AC"
            borderRadius={parseInt(width) <= 240 ? "full" : "md"}
            fontSize="xs"
            fontWeight="bold"
            boxShadow={isInWatchLater ? "0 0 8px rgba(77, 182, 172, 0.9)" : "0 0 5px rgba(77, 182, 172, 0.7)"}
            transition="all 0.3s"
            textShadow={parseInt(width) > 240 ? "0 0 5px rgba(77, 182, 172, 0.7)" : undefined}
            _hover={{ 
              bg: "rgba(77, 182, 172, 0.8)",
              color: "white",
              transform: "scale(1.1)",
              boxShadow: "0 0 10px #4DB6AC",
            }}
          >
            {parseInt(width) <= 240 ? <FaClock size="9px" /> : (isInWatchLater ? "✓ Saved" : "+ Later")}
          </Box>
        </Box>

        {/* No play button as requested */}

        {/* Description Slide-Up on Hover */}
        {(video.shortDescription || video.description || video.longDescription) && (
          <>
            {/* Mobile version - static display without animation */}
            {isMobile && (
              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                bg="rgba(0,0,0,0.8)"
                color="white"
                p={2}
                fontSize="xs"
                opacity={effectiveHoverState ? 1 : 0}
                transition="opacity 0.2s ease-out"
                maxHeight="100%"
                overflowY="auto"
                zIndex={2}
              >
                <Text fontSize="10px" noOfLines={3}>
                  {video.shortDescription || video.description || video.longDescription || "No description available"}
                </Text>
              </Box>
            )}
            
            {/* Desktop version - with slide-up animation */}
            {!isMobile && (
              <Box
                position="absolute"
                bottom="0"
                left="0"
                right="0"
                bg="rgba(0,0,0,0.8)"
                color="white"
                p={2}
                fontSize="xs"
                maxHeight="100%"
                overflowY="auto"
                zIndex={2}
                style={{
                  transform: effectiveHoverState ? 'translateY(0)' : 'translateY(20px)',
                  opacity: effectiveHoverState ? 1 : 0,
                  transition: 'transform 0.35s ease-out, opacity 0.35s ease-out'
                }}
              >
                <Text fontSize={{ base: "10px", md: "xs" }} noOfLines={4}>
                  {video.shortDescription || video.description || video.longDescription || "No description available"}
                </Text>
              </Box>
            )}
          </>
        )}
        
        
        {/* Error Badge */}
        {imageError && (
          <Box
            position="absolute"
            top="5px"
            left="5px"
            bg="red.500"
            color="white"
            px={1}
            borderRadius="sm"
            fontSize="xs"
          >
            Image Error
          </Box>
        )}
      </Box>
      
      {/* Title Row with Video Type Classification */}
      <Box display="flex" justifyContent="space-between" alignItems="center" position="relative">
        <Text
          mt={1}
          mb={1}
          minHeight={titleHeight}
          fontSize={fontSize}
          color="gray.800"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          cursor="pointer"
          onClick={handleClick}
          fontWeight="500"
          lineHeight={titleHeight}
          flex="1"
        >
          {video.title || "Untitled Video"}
        </Text>
        
        {/* Video Type display - with slide-in/out animation */}
        <Box 
          position="absolute" 
          right="0" 
          display="flex" 
          alignItems="center" 
          overflow="hidden"
          height="24px"
        >
          <Badge 
            colorScheme={getTypeBadgeColor(videoType)} 
            fontSize="0.6em"
            textTransform="capitalize"
            variant="subtle"
            opacity={(isRowHovered || isHovering || forceHover) && videoType ? 1 : 0}
            transform={(isRowHovered || isHovering || forceHover) && videoType ? "translateX(0)" : "translateX(150%)"}
            sx={{
              '@keyframes slideInType': {
                from: { transform: 'translateX(150%)', opacity: 0 },
                to: { transform: 'translateX(0)', opacity: 1 }
              },
              '@keyframes slideOutType': {
                from: { transform: 'translateX(0)', opacity: 1 },
                to: { transform: 'translateX(150%)', opacity: 0 }
              },
              animation: (isRowHovered || isHovering || forceHover) && videoType
                ? 'slideInType 0.4s ease-out forwards'
                : 'slideOutType 0.4s ease-out forwards',
              transition: 'opacity 0.4s ease-out, transform 0.4s ease-out'
            }}
          >
            {videoType}
          </Badge>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCard;

// All state updates in this component appear to be related to user interactions (handleClick, etc.)
// and image loading states which don't typically cause infinite loops.
// There are no suspicious useEffect hooks in this component causing setState operations.
